import { Box, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { type FC, Fragment, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { HOURS_SUMMARY } from "../../../theme/tokens";
import type { HoursSummaryEmployeeDayShift } from "./types";
import { DAY_NAMES } from "./utils";

type ShiftBadgeData = { shiftId: string; dayName: string; date: string; shiftStart: string; shiftEnd: string };
type ExtraPaySummary = { dailyOt: number; weeklyOt: number; overrun: number; agency: number };

type ShiftDetailRow = {
	id: string;
	label: string;
	rowType: "cycleHeader" | "shifts" | "extraPay";
	week1Shifts: ShiftBadgeData[];
	week2Shifts: ShiftBadgeData[];
	week1ExtraPay: ExtraPaySummary;
	week2ExtraPay: ExtraPaySummary;
};

const EMPTY_EXTRA_PAY: ExtraPaySummary = { dailyOt: 0, weeklyOt: 0, overrun: 0, agency: 0 };

const EmptyCell: FC = () => (
	<Typography
		variant="caption"
		sx={{
			color: "text.disabled",
		}}
	>
		—
	</Typography>
);

const ShiftBadges: FC<{ shifts: ShiftBadgeData[] }> = ({ shifts }) => {
	if (shifts.length === 0) return <EmptyCell />;
	return (
		<Box
			sx={{
				display: "flex",
				flexWrap: "wrap",
				gap: 0.5,
				alignItems: "center",
			}}
		>
			{shifts.map((s) => (
				<Box key={s.shiftId} sx={{ bgcolor: "grey.100", borderRadius: "6px", px: 1, py: 0.25 }}>
					<Typography
						variant="caption"
						sx={{
							fontWeight: 600,
							fontSize: "0.75rem",
						}}
					>
						{s.dayName} {s.shiftStart}-{s.shiftEnd}
					</Typography>
				</Box>
			))}
		</Box>
	);
};

const ExtraPayBadges: FC<{ extraPay: ExtraPaySummary; isActual: boolean }> = ({ extraPay, isActual }) => {
	const { t } = useTranslation();
	const badges: { label: string; value: number; color: string; show: boolean }[] = [
		{ label: t("hours.summary.daily.ot"), value: extraPay.dailyOt, color: HOURS_SUMMARY.DAILY_OT, show: extraPay.dailyOt > 0 },
		{ label: t("hours.summary.weekly.ot"), value: extraPay.weeklyOt, color: HOURS_SUMMARY.WEEKLY_OT, show: extraPay.weeklyOt > 0 },
		{ label: t("hours.summary.shift.overrun"), value: extraPay.overrun, color: HOURS_SUMMARY.OVERRUN, show: isActual && extraPay.overrun !== 0 },
		{ label: t("hours.summary.agency.hours"), value: extraPay.agency, color: HOURS_SUMMARY.AGENCY, show: extraPay.agency > 0 },
	];
	const visible = badges.filter((b) => b.show);
	if (visible.length === 0) return <EmptyCell />;
	return (
		<Box
			sx={{
				display: "flex",
				flexWrap: "wrap",
				gap: 0.5,
				alignItems: "center",
			}}
		>
			{visible.map((b) => (
				<Box key={b.label} sx={{ bgcolor: `${b.color}14`, borderRadius: "6px", px: 0.75, py: 0.25 }}>
					<Typography variant="caption" sx={{ color: b.color, fontWeight: 600, fontSize: "0.7rem", whiteSpace: "nowrap" }}>
						{b.label} {Math.round(b.value * 10) / 10}h
					</Typography>
				</Box>
			))}
		</Box>
	);
};

export const WeeklyShiftView: FC<{
	shifts: HoursSummaryEmployeeDayShift[];
	isActual: boolean;
	sinceDate: DateTime;
}> = ({ shifts, isActual, sinceDate }) => {
	const { t } = useTranslation();

	const rows = useMemo(() => {
		const daysSinceSunday = sinceDate.weekday === 7 ? 0 : sinceDate.weekday;
		const firstSunday = sinceDate.minus({ days: daysSinceSunday }).startOf("day");

		const cycleMap = new Map<
			number,
			{ cycleStart: DateTime; cycleEnd: DateTime; week1: Map<number, HoursSummaryEmployeeDayShift[]>; week2: Map<number, HoursSummaryEmployeeDayShift[]> }
		>();

		for (const shift of shifts) {
			const shiftDate = DateTime.fromISO(shift.date);
			const delta = Math.floor(shiftDate.diff(firstSunday, "days").days);
			const cycleIndex = Math.floor(delta / 14);
			const posInCycle = delta % 14;
			const weekInCycle = Math.floor(posInCycle / 7);
			const dayOfWeek = posInCycle % 7;

			let cycle = cycleMap.get(cycleIndex);
			if (!cycle) {
				const cycleStart = firstSunday.plus({ days: cycleIndex * 14 });
				cycle = { cycleStart, cycleEnd: cycleStart.plus({ days: 13 }), week1: new Map(), week2: new Map() };
				cycleMap.set(cycleIndex, cycle);
			}

			const week = weekInCycle === 0 ? cycle.week1 : cycle.week2;
			const existing = week.get(dayOfWeek) || [];
			existing.push(shift);
			week.set(dayOfWeek, existing);
		}

		const flattenWeek = (week: Map<number, HoursSummaryEmployeeDayShift[]>): ShiftBadgeData[] =>
			Array.from(week.entries())
				.sort(([a], [b]) => a - b)
				.flatMap(([dayIdx, dayShifts]) =>
					dayShifts.map((s) => ({ shiftId: s.shiftId, dayName: DAY_NAMES[dayIdx], date: s.date, shiftStart: s.shiftStart, shiftEnd: s.shiftEnd })),
				);

		const calcExtraPay = (week: Map<number, HoursSummaryEmployeeDayShift[]>): ExtraPaySummary => {
			const result: ExtraPaySummary = { dailyOt: 0, weeklyOt: 0, overrun: 0, agency: 0 };
			for (const shifts of week.values()) {
				for (const sh of shifts) {
					result.dailyOt += sh.dailyOtHours;
					result.weeklyOt += sh.weeklyOtHours;
					result.overrun += sh.shiftOverrunHours;
					result.agency += sh.agencyHours;
				}
			}
			return result;
		};

		const result: ShiftDetailRow[] = [];
		const sortedCycles = Array.from(cycleMap.entries()).sort(([a], [b]) => a - b);

		for (const [idx, cycle] of sortedCycles) {
			const dateLabel = `${cycle.cycleStart.toFormat("MMM d")} - ${cycle.cycleEnd.toFormat("MMM d, yyyy")}`;
			const w1Shifts = flattenWeek(cycle.week1);
			const w2Shifts = flattenWeek(cycle.week2);
			const w1Extra = calcExtraPay(cycle.week1);
			const w2Extra = calcExtraPay(cycle.week2);

			result.push({
				id: `header-${idx}`,
				label: dateLabel,
				rowType: "cycleHeader",
				week1Shifts: [],
				week2Shifts: [],
				week1ExtraPay: EMPTY_EXTRA_PAY,
				week2ExtraPay: EMPTY_EXTRA_PAY,
			});
			result.push({
				id: `shifts-${idx}`,
				label: t("hours.summary.shifts"),
				rowType: "shifts",
				week1Shifts: w1Shifts,
				week2Shifts: w2Shifts,
				week1ExtraPay: EMPTY_EXTRA_PAY,
				week2ExtraPay: EMPTY_EXTRA_PAY,
			});
			result.push({
				id: `extra-${idx}`,
				label: t("hours.summary.extra.pay.hours"),
				rowType: "extraPay",
				week1Shifts: [],
				week2Shifts: [],
				week1ExtraPay: w1Extra,
				week2ExtraPay: w2Extra,
			});
		}
		return result;
	}, [shifts, sinceDate, t]);

	if (shifts.length === 0) {
		return (
			<Typography
				variant="body2"
				sx={{
					color: "text.secondary",
					textAlign: "center",
					py: 1,
				}}
			>
				{t("hours.summary.no.data")}
			</Typography>
		);
	}

	return (
		<Box
			sx={{
				display: "grid",
				gridTemplateColumns: "minmax(110px, 160px) 1fr 1fr",
				columnGap: 2,
				rowGap: 0.75,
				alignItems: "start",
			}}
		>
			<Box />
			<Typography variant="caption" sx={{ fontWeight: 700, fontSize: "0.75rem", color: "text.secondary" }}>
				{t("hours.summary.week")} 1
			</Typography>
			<Typography variant="caption" sx={{ fontWeight: 700, fontSize: "0.75rem", color: "text.secondary" }}>
				{t("hours.summary.week")} 2
			</Typography>
			{rows.map((row) => {
				if (row.rowType === "cycleHeader") {
					return (
						<Box key={row.id} sx={{ gridColumn: "1 / -1", bgcolor: "primary.50", borderRadius: 1, px: 1, py: 0.5, mt: 0.5 }}>
							<Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary" }}>
								{row.label}
							</Typography>
						</Box>
					);
				}
				const isShiftsRow = row.rowType === "shifts";
				return (
					<Fragment key={row.id}>
						<Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", whiteSpace: "nowrap" }}>
							{row.label}
						</Typography>
						{isShiftsRow ? <ShiftBadges shifts={row.week1Shifts} /> : <ExtraPayBadges extraPay={row.week1ExtraPay} isActual={isActual} />}
						{isShiftsRow ? <ShiftBadges shifts={row.week2Shifts} /> : <ExtraPayBadges extraPay={row.week2ExtraPay} isActual={isActual} />}
					</Fragment>
				);
			})}
		</Box>
	);
};
